import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useRecoilValue } from "recoil";
import { userState } from "../../recoil/auth";
import { getGuidePost, getGuidePostUrl } from "./guidePosts";
import "./Guide.css";

const INTERESTS = ["스마트스토어", "플레이스", "쿠팡"];

const getEventPeriodLabel = (date = new Date()) => {
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const range =
    day < 15 ? `${month}.1 ~ ${month}.14` : `${month}.15 ~ ${month}.${lastDay}`;
  return `${range} 한정`;
};

const KAKAO_CHAT_URL = "https://pf.kakao.com/_xozqxin/chat";

const copyText = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const area = document.createElement("textarea");
  area.value = text;
  area.setAttribute("readonly", "");
  area.style.position = "fixed";
  area.style.left = "-9999px";
  document.body.appendChild(area);
  area.select();
  document.execCommand("copy");
  document.body.removeChild(area);
};

const GuideApplyForm = () => {
  const [interest, setInterest] = useState(INTERESTS[0]);
  const [storeName, setStoreName] = useState("");
  const [phone, setPhone] = useState("");
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [marketingAgreed, setMarketingAgreed] = useState(false);
  const [formError, setFormError] = useState("");
  const [copyOpen, setCopyOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(5);
  const [chatBlocked, setChatBlocked] = useState(false);
  const chatUrlRef = useRef("");

  const openKakaoChat = () => {
    if (!chatUrlRef.current) return false;
    const opened = window.open(
      chatUrlRef.current,
      "olzoomKakaoChat",
      "width=480,height=720"
    );
    return Boolean(opened);
  };

  useEffect(() => {
    if (!copyOpen || chatBlocked) return undefined;
    if (secondsLeft === 0) {
      if (openKakaoChat()) {
        setCopyOpen(false);
      } else {
        setChatBlocked(true);
      }
      return undefined;
    }
    const timer = setTimeout(() => setSecondsLeft((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [copyOpen, secondsLeft, chatBlocked]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!storeName.trim() || !phone.trim()) {
      setFormError("스토어명과 휴대폰번호를 입력해주세요.");
      return;
    }
    if (!privacyAgreed) {
      setFormError("개인정보 수집 동의가 필요합니다.");
      return;
    }
    const chatUrl = KAKAO_CHAT_URL;

    const text = [
      "무료 진단 + 10일 무료 테스트 신청",
      `스토어명: ${storeName.trim()}`,
      `휴대폰번호: ${phone.trim()}`,
      `관심 항목: ${interest}`,
      "개인정보 수집 동의: 동의",
      `마케팅 수신 동의: ${marketingAgreed ? "동의" : "미동의"}`,
    ].join("\n");

    try {
      await copyText(text);
    } catch (copyError) {
      setFormError("신청 내용을 복사하지 못했습니다. 다시 시도해주세요.");
      return;
    }

    chatUrlRef.current = chatUrl;
    setFormError("");
    setChatBlocked(false);
    setSecondsLeft(5);
    setCopyOpen(true);
  };

  const closeCopyPopup = () => {
    chatUrlRef.current = "";
    setCopyOpen(false);
  };

  return (
    <div className="guide-phone">
      <span className="guide-phone-btn is-silent" />
      <span className="guide-phone-btn is-vol" />
      <span className="guide-phone-btn is-power" />
      <div className="guide-phone-screen">
        <div className="guide-phone-status">
          <span className="guide-phone-time">9:30</span>
          <span className="guide-phone-island">
            <span className="guide-phone-island-dot" />
          </span>
          <span className="guide-phone-status-icons" aria-hidden="true">
            <svg viewBox="0 0 18 12" className="is-signal">
              <rect x="0" y="8" width="3" height="4" rx="0.6" />
              <rect x="5" y="5" width="3" height="7" rx="0.6" />
              <rect x="10" y="2" width="3" height="10" rx="0.6" />
              <rect x="15" y="0" width="3" height="12" rx="0.6" />
            </svg>
            <svg viewBox="0 0 16 12" className="is-wifi">
              <path d="M8 10.2a1.2 1.2 0 1 0 0 2.4 1.2 1.2 0 0 0 0-2.4Z" />
              <path d="M3.4 8.2a6.4 6.4 0 0 1 9.2 0" />
              <path d="M1 5.4a9.8 9.8 0 0 1 14 0" />
            </svg>
            <svg viewBox="0 0 26 12" className="is-battery">
              <rect x="0.6" y="0.6" width="21" height="10.8" rx="2.2" />
              <rect x="2.2" y="2.2" width="14" height="7.6" rx="1" />
              <path d="M23 4.2h1.4a1 1 0 0 1 1 1v1.6a1 1 0 0 1-1 1H23" />
            </svg>
          </span>
        </div>
        <form className="guide-apply-card" onSubmit={handleSubmit}>
          <header className="guide-apply-intro">
            <div className="guide-event-marks">
              <span className="guide-event-mark">한정 이벤트</span>
              <span className="guide-event-mark is-live">진행중</span>
            </div>
            <h2>무료 테스트 신청</h2>
            <p>
              10일 무료 테스트를 통해,{" "}
              <strong>검색 노출 순위 상승</strong>과
              <br />
              <strong>실제 매출 변화</strong>를 확인해보세요!
            </p>
          </header>

          <div className="guide-apply-fields">
            <p>신청 정보</p>
            <label>
              스토어명
              <input
                type="text"
                placeholder="올려줌몰"
                value={storeName}
                onChange={(e) => setStoreName(e.target.value)}
              />
            </label>
            <label>
              휴대폰번호
              <input
                type="tel"
                placeholder="010-1234-5678"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </label>
            <div className="guide-interest">
              <span>관심 항목</span>
              <div className="guide-interest-segment">
                {INTERESTS.map((item) => (
                  <button
                    key={item}
                    type="button"
                    className={interest === item ? "is-selected" : ""}
                    onClick={() => setInterest(item)}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="guide-apply-consent">
            <label className="guide-check">
              <input
                type="checkbox"
                checked={privacyAgreed}
                onChange={(e) => setPrivacyAgreed(e.target.checked)}
              />
              <span>개인정보 수집 동의</span>
            </label>
            <label className="guide-check">
              <input
                type="checkbox"
                checked={marketingAgreed}
                onChange={(e) => setMarketingAgreed(e.target.checked)}
              />
              <span>마케팅 수신 동의(선택)</span>
            </label>
          </div>

          <div className="guide-apply-action">
            <p className="guide-event-bubble">{getEventPeriodLabel()}</p>
            <button type="submit" className="guide-apply-submit">
              무료 진단 + 10일 무료 테스트
            </button>
            {formError && <p className="guide-apply-error">{formError}</p>}
          </div>
        </form>
        {copyOpen &&
          createPortal(
            <div className="guide-copy-overlay" role="dialog" aria-modal="true">
              <div className="guide-copy-popup">
                {chatBlocked ? (
                  <button
                    type="button"
                    className="guide-copy-open"
                    onClick={() => {
                      if (openKakaoChat()) setCopyOpen(false);
                    }}
                  >
                    카카오톡 열기
                  </button>
                ) : (
                  <p className="guide-copy-timer">{secondsLeft}</p>
                )}
                <p>
                  내용을 복사했습니다.
                  <br />
                  카카오톡 채팅창에 붙여넣기 후 전송해 주세요.
                </p>
                <button type="button" className="guide-copy-close" onClick={closeCopyPopup}>
                  닫기
                </button>
              </div>
            </div>,
            document.body
          )}
        <span className="guide-phone-bar" />
      </div>
    </div>
  );
};

const GuideArticlePage = () => {
  const { slug } = useParams();
  const post = getGuidePost(slug);
  const user = useRecoilValue(userState);
  const phoneRef = useRef(null);

  if (!post) {
    return (
      <div className="guide-page">
        <Helmet>
          <title>글을 찾을 수 없습니다 | 올려줌</title>
          <meta name="robots" content="noindex,nofollow" />
        </Helmet>
        <main className="guide-main">
          <h1>글을 찾을 수 없습니다</h1>
          <Link to="/guide" className="guide-back">
            마케팅 게시글
          </Link>
        </main>
      </div>
    );
  }

  const canonicalUrl = getGuidePostUrl(post.slug);
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.description,
    datePublished: post.publishedAt,
    inLanguage: "ko-KR",
    mainEntityOfPage: canonicalUrl,
    author: {
      "@type": "Organization",
      name: "올려줌",
      url: "https://www.olzoom.com/",
    },
    publisher: {
      "@type": "Organization",
      name: "올려줌",
      url: "https://www.olzoom.com/",
    },
  };

  const scrollToPhone = () => {
    phoneRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className={`guide-page guide-detail${user ? " is-logged-in" : ""}`}>
      <Helmet>
        <title>{`${post.title} | 올려줌`}</title>
        <meta name="description" content={post.description} />
        <meta name="keywords" content={post.keywords} />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={`${post.title} | 올려줌`} />
        <meta property="og:description" content={post.description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="올려줌" />
        <meta property="og:locale" content="ko_KR" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="guide-scroll-card">
        <div className="guide-scroll-inner">
          <article className="guide-body guide-article">
            <h1>{post.title}</h1>
            {!user && (
              <button
                type="button"
                className="guide-event-jump"
                onClick={scrollToPhone}
              >
                <span>한정 이벤트 · {getEventPeriodLabel()}</span>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M9 3.5v9.2M4.8 9.2 9 13.8l4.2-4.6"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
            <time dateTime={post.publishedAt}>{post.publishedAt}</time>
            {post.sections.map((section) => (
              <section key={section.heading}>
                <h2>{section.heading}</h2>
                {section.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </section>
            ))}
            {user && (
              <div className="guide-register">
                <Link to="/?tab=addAdvertisement">상품 등록 하러가기</Link>
              </div>
            )}
          </article>
          {!user && (
            <div ref={phoneRef} className="guide-phone-slot">
              <GuideApplyForm />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuideArticlePage;
